import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  currentStep: 'START',
  currentStepIndex: 0,
  currentSubstepIndex: 0,
  helperDetails: {
    basicInfo: {
      fullName: '',
      nationality: '',
      passportNumber: '',
      birthDate: '',
      gender: '',
      maritalStatus: '',
      contactNumber: ''
    },
    employmentInfo: {
      previousExperience: '',
      experienceYears: '',
      salary: 0,
      contractDuration: '',
      skills: []
    },
    additionalInfo: {
      healthStatus: '',
      medicalConditions: [],
      emergencyContact: {
        name: '',
        relationship: '',
        phone: ''
      }
    }
  },
  employerDetails: {
    type: 'individual', // 'individual' or 'company'
    personalInfo: {
      fullName: '',
      cprNumber: '',
      contactNumber: '',
      email: ''
    },
    companyInfo: {
      companyName: '',
      crNumber: '',
      contactPerson: '',
      contactNumber: '',
      email: ''
    },
    address: {
      building: '',
      road: '',
      block: '',
      city: '',
      governorate: ''
    }
  },
  contractDetails: {
    startDate: '',
    workLocation: '',
    duties: [],
    workingHours: '',
    restDays: [],
    sponsorRelationship: ''
  },
  selectedPlan: null,
  coverageOptions: {
    medicalCoverageLimit: '10000', // Default 10K BHD
    repatriationCoverage: true,
    personalAccidentCoverage: true,
    thirdPartyLiability: true,
    runawayHelperCoverage: false,
    additionalAddons: []
  },
  uploadedDocuments: {
    helperPassport: null,
    medicalCertificate: null,
    employmentContract: null,
    employerCPR: null,
    helperPhoto: null,
    visaDocuments: null,
    previousInsurance: null
  },
  quote: null,
  promoCode: null,
  totalSteps: 13,
  isLoading: false,
  error: null
};

const domesticHelperFlowSlice = createSlice({
  name: 'domesticHelperFlow',
  initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setCurrentStepIndex: (state, action) => {
      state.currentStepIndex = action.payload;
    },
    setCurrentSubstepIndex: (state, action) => {
      state.currentSubstepIndex = action.payload;
    },
    updateHelperBasicInfo: (state, action) => {
      state.helperDetails.basicInfo = {
        ...state.helperDetails.basicInfo,
        ...action.payload
      };
    },
    updateHelperEmploymentInfo: (state, action) => {
      state.helperDetails.employmentInfo = {
        ...state.helperDetails.employmentInfo,
        ...action.payload
      };
    },
    updateHelperAdditionalInfo: (state, action) => {
      state.helperDetails.additionalInfo = {
        ...state.helperDetails.additionalInfo,
        ...action.payload
      };
    },
    updateEmployerDetails: (state, action) => {
      const { type, data } = action.payload;
      if (type === 'personal') {
        state.employerDetails.personalInfo = {
          ...state.employerDetails.personalInfo,
          ...data
        };
      } else if (type === 'company') {
        state.employerDetails.companyInfo = {
          ...state.employerDetails.companyInfo,
          ...data
        };
      } else if (type === 'address') {
        state.employerDetails.address = {
          ...state.employerDetails.address,
          ...data
        };
      } else {
        state.employerDetails = {
          ...state.employerDetails,
          ...action.payload
        };
      }
    },
    updateContractDetails: (state, action) => {
      state.contractDetails = {
        ...state.contractDetails,
        ...action.payload
      };
    },
    setSelectedPlan: (state, action) => {
      state.selectedPlan = action.payload;
    },
    updateCoverageOptions: (state, action) => {
      state.coverageOptions = {
        ...state.coverageOptions,
        ...action.payload
      };
    },
    uploadDocument: (state, action) => {
      const { documentType, file } = action.payload;
      state.uploadedDocuments[documentType] = file;
    },
    removeDocument: (state, action) => {
      const { documentType } = action.payload;
      state.uploadedDocuments[documentType] = null;
    },
    setQuote: (state, action) => {
      state.quote = action.payload;
    },
    setPromoCode: (state, action) => {
      state.promoCode = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    resetDomesticHelperFlow: (state) => {
      return initialState;
    },
    // Navigation helpers
    nextStep: (state) => {
      if (state.currentStepIndex < state.totalSteps - 1) {
        state.currentStepIndex += 1;
        state.currentSubstepIndex = 0;
      }
    },
    previousStep: (state) => {
      if (state.currentStepIndex > 0) {
        state.currentStepIndex -= 1;
        // When going back to a step with substeps, go to the last substep
        // START step has substeps ['basic_info', 'documents_ready'], so set to last one (index 1)
        if (state.currentStepIndex === 0) {
          state.currentSubstepIndex = 1; // Go to 'documents_ready'
        } else {
          state.currentSubstepIndex = 0;
        }
      }
    },
    nextSubstep: (state) => {
      state.currentSubstepIndex += 1;
    },
    previousSubstep: (state) => {
      if (state.currentSubstepIndex > 0) {
        state.currentSubstepIndex -= 1;
      }
    }
  }
});

export const {
  setCurrentStep,
  setCurrentStepIndex,
  setCurrentSubstepIndex,
  updateHelperBasicInfo,
  updateHelperEmploymentInfo,
  updateHelperAdditionalInfo,
  updateEmployerDetails,
  updateContractDetails,
  setSelectedPlan,
  updateCoverageOptions,
  uploadDocument,
  removeDocument,
  setQuote,
  setPromoCode,
  setIsLoading,
  setError,
  resetDomesticHelperFlow,
  nextStep,
  previousStep,
  nextSubstep,
  previousSubstep
} = domesticHelperFlowSlice.actions;

// Selectors
export const selectDomesticHelperFlow = (state) => state.domesticHelperFlow;
export const selectCurrentStep = (state) => state.domesticHelperFlow.currentStep;
export const selectCurrentStepIndex = (state) => state.domesticHelperFlow.currentStepIndex;
export const selectHelperDetails = (state) => state.domesticHelperFlow.helperDetails;
export const selectEmployerDetails = (state) => state.domesticHelperFlow.employerDetails;
export const selectSelectedPlan = (state) => state.domesticHelperFlow.selectedPlan;
export const selectUploadedDocuments = (state) => state.domesticHelperFlow.uploadedDocuments;
export const selectQuote = (state) => state.domesticHelperFlow.quote;

export default domesticHelperFlowSlice.reducer;